import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Usuario {
  id_usuario: number;
  nombre: string;
  apellido: string;
  email?: string;
  nombre_usuario: string;
  biografia?: string;
  foto_perfil?: string;
  foto_portada?: string;
  es_perfil_publico: boolean;
  fecha_registro?: string;
  estadisticas?: {
    total_seguidores: number;
    total_seguidos: number;
    total_amigos: number;
    total_publicaciones: number;
    total_comentarios: number;
    total_megustas_recibidos: number;
  };
  lista_seguidores?: any[];
  lista_seguidos?: any[];
}

export interface Publicacion {
  id_publicacion: number;
  id_usuario: number;
  contenido: string;
  tipo_publicacion: 'texto' | 'imagen' | 'video' | 'enlace';
  imagen_url?: string;
  enlace_externo?: string;
  es_publica: boolean;
  fecha_creacion: string;
  fecha_actualizacion?: string | null;
  activa: boolean;
  es_anuncio_admin: boolean;
  prioridad_anuncio: number;
  fecha_inicio_anuncio?: string | null;
  fecha_fin_anuncio?: string | null;
  usuario: {
    id_usuario: number;
    nombre: string;
    apellido: string;
    nombre_usuario: string;
    foto_perfil?: string;
  };
  total_megustas: number;
  total_comentarios: number;
  le_dio_megusta?: boolean;
  tipo_reaccion_usuario?: string | null;
  reacciones_por_tipo?: {
    megusta: number;
    amor: number;
    risa: number;
    asombro: number;
    triste: number;
    enojado: number;
  };
  comentarios?: Comentario[];
}

export interface Comentario {
  id_comentario: number;
  id_publicacion: number;
  id_usuario: number;
  contenido: string;
  id_comentario_padre?: number | null;
  fecha_creacion: string;
  fecha_actualizacion?: string | null;
  activo: boolean;
  usuario: {
    id_usuario: number;
    nombre: string;
    apellido: string;
    nombre_usuario: string;
    foto_perfil?: string;
  };
  total_megustas: number;
  le_dio_megusta?: boolean;
  mi_reaccion?: string | null;
  respuestas?: Comentario[];
}

export interface MiPerfilResponse {
  ok: boolean;
  usuario: Usuario;
  publicaciones: {
    total: number;
    pagina: number;
    total_paginas: number;
    datos: Publicacion[];
  };
}

export interface Conversacion {
  id_conversacion: number;
  tipo_conversacion: 'individual' | 'grupal';
  nombre_grupo?: string;
  fecha_creacion: string;
  fecha_ultimo_mensaje?: string;
  ultimo_mensaje?: {
    contenido: string;
    tipo_mensaje: string;
    id_usuario: number;
    fecha_creacion: string;
  };
  participantes: Usuario[];
  mensajes_no_leidos: number;
  silenciada: boolean;
}

export interface Mensaje {
  id_mensaje: number;
  id_conversacion: number;
  id_usuario: number;
  contenido: string;
  tipo_mensaje: 'texto' | 'imagen' | 'archivo' | 'audio' | 'video';
  archivo_url?: string;
  fecha_creacion: string;
  fecha_actualizacion?: string;
  activo: boolean;
  usuario: {
    id_usuario: number;
    nombre: string;
    apellido: string;
    foto_perfil?: string;
  };
  leido_por?: any[];
}

export interface Notificacion {
  id_notificacion: number;
  id_usuario_receptor: number;
  id_usuario_emisor?: number;
  tipo_notificacion: string;
  titulo: string;
  mensaje: string;
  leida: boolean;
  fecha_creacion: string;
  fecha_lectura?: string;
  enlace?: string;
  usuarioEmisor?: Usuario;
}

@Injectable({
  providedIn: 'root'
})
export class ComunidadService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  // ==================== PERFIL ====================

  obtenerMiPerfil(): Observable<MiPerfilResponse> {
    return this.http.get<MiPerfilResponse>(`${this.baseUrl}/perfil/me/mi-perfil`);
  }

  actualizarPerfil(datos: Partial<Usuario>): Observable<any> {
    return this.http.put(`${this.baseUrl}/perfil/me/actualizar`, datos);
  }

  subirFotoPerfil(archivo: File): Observable<any> {
    const formData = new FormData();
    formData.append('imagen', archivo);
    return this.http.post(`${this.baseUrl}/perfil/me/foto-perfil`, formData);
  }

  subirFotoPortada(archivo: File): Observable<any> {
    const formData = new FormData();
    formData.append('imagen', archivo);
    return this.http.post(`${this.baseUrl}/perfil/me/foto-portada`, formData);
  }

  obtenerPerfilUsuario(idUsuario: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/perfil/${idUsuario}`);
  }

  obtenerPerfilCompleto(idUsuario: number, limitePublicaciones: number = 10, paginaPublicaciones: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/perfil/completo/${idUsuario}`, {
      params: {
        limite_publicaciones: limitePublicaciones.toString(),
        pagina_publicaciones: paginaPublicaciones.toString()
      }
    });
  }

  buscarUsuarios(query: string, limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/perfil/buscar`, {
      params: { q: query, limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  // ==================== SEGUIDORES ====================

  seguirUsuario(idUsuario: number): Observable<any> {
    return this.http.post(`${this.baseUrl}/perfil/seguir/${idUsuario}`, {});
  }

  dejarDeSeguir(idUsuario: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/perfil/dejar-seguir/${idUsuario}`);
  }

  obtenerSeguidores(idUsuario: number | string, limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/perfil/${idUsuario}/seguidores`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  obtenerMisSeguidores(limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/perfil/me/seguidores`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  obtenerSeguidos(idUsuario: number | string, limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/perfil/${idUsuario}/seguidos`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  obtenerMisSeguidos(limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/perfil/me/seguidos`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  // ==================== PUBLICACIONES ====================

  crearPublicacion(datos: {
    contenido: string;
    tipo_publicacion: 'texto' | 'imagen' | 'video' | 'enlace';
    es_publica?: boolean;
    enlace_externo?: string;
    imagen?: File;
  }): Observable<any> {
    const formData = new FormData();
    formData.append('contenido', datos.contenido);
    formData.append('tipo_publicacion', datos.tipo_publicacion);
    formData.append('es_publica', datos.es_publica !== undefined ? String(datos.es_publica) : 'true');

    if (datos.enlace_externo) {
      formData.append('enlace_externo', datos.enlace_externo);
    }

    if (datos.imagen) {
      formData.append('imagen', datos.imagen);
    }

    return this.http.post(`${this.baseUrl}/publicaciones-comunidad`, formData);
  }

  subirImagenPublicacion(archivo: File): Observable<any> {
    const formData = new FormData();
    formData.append('imagen', archivo);
    return this.http.post(`${this.baseUrl}/publicaciones-comunidad/subir-imagen`, formData);
  }

  obtenerFeed(limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/publicaciones-comunidad/feed`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  obtenerPublicacion(idPublicacion: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/publicaciones-comunidad/${idPublicacion}`);
  }

  obtenerPublicacionesUsuario(idUsuario: number, limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/publicaciones-comunidad/usuario/${idUsuario}`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  actualizarPublicacion(idPublicacion: number, datos: { contenido?: string; es_publica?: boolean }): Observable<any> {
    return this.http.put(`${this.baseUrl}/publicaciones-comunidad/${idPublicacion}`, datos);
  }

  eliminarPublicacion(idPublicacion: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/publicaciones-comunidad/${idPublicacion}`);
  }

  // ==================== COMENTARIOS ====================

  crearComentario(idPublicacion: number, contenido: string, idComentarioPadre?: number, imagen?: File): Observable<any> {
    if (imagen) {
      const formData = new FormData();
      if (contenido) formData.append('contenido', contenido);
      formData.append('imagen', imagen);
      if (idComentarioPadre) formData.append('id_comentario_padre', String(idComentarioPadre));
      return this.http.post(`${this.baseUrl}/publicaciones-comunidad/${idPublicacion}/comentarios`, formData);
    }
    const body: any = { contenido };
    if (idComentarioPadre) {
      body.id_comentario_padre = idComentarioPadre;
    }
    return this.http.post(`${this.baseUrl}/publicaciones-comunidad/${idPublicacion}/comentarios`, body);
  }

  obtenerComentarios(idPublicacion: number, limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/publicaciones-comunidad/${idPublicacion}/comentarios`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  actualizarComentario(idComentario: number, contenido: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/publicaciones-comunidad/comentarios/${idComentario}`, { contenido });
  }

  eliminarComentario(idComentario: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/publicaciones-comunidad/comentarios/${idComentario}`);
  }

  /** Toggle reacción en un comentario (crea/actualiza/elimina según toggle inteligente) */
  reaccionarComentarioComunidad(idComentario: number, tipoReaccion: string): Observable<any> {
    return this.http.post(
      `${this.baseUrl}/publicaciones-comunidad/comentarios/${idComentario}/reaccion`,
      { tipo_reaccion: tipoReaccion }
    );
  }

  // ==================== REACCIONES ====================

  reaccionarPublicacion(idPublicacion: number, tipoReaccion: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/megustas/publicacion/${idPublicacion}`, { tipo_reaccion: tipoReaccion });
  }

  obtenerReaccionesPublicacion(idPublicacion: number, tipoReaccion?: string): Observable<any> {
    const params: any = {};
    if (tipoReaccion) {
      params.tipo_reaccion = tipoReaccion;
    }
    return this.http.get(`${this.baseUrl}/megustas/publicacion/${idPublicacion}`, { params });
  }

  reaccionarComentario(idComentario: number, tipoReaccion: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/megustas/comentario/${idComentario}`, { tipo_reaccion: tipoReaccion });
  }

  obtenerReaccionesComentario(idComentario: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/megustas/comentario/${idComentario}`);
  }

  // ==================== CHAT ====================

  crearConversacion(tipoConversacion: 'individual' | 'grupal', idParticipantes: number[], nombreGrupo?: string): Observable<any> {
    const body: any = {
      tipo_conversacion: tipoConversacion,
      id_participantes: idParticipantes
    };
    if (nombreGrupo) {
      body.nombre_grupo = nombreGrupo;
    }
    return this.http.post(`${this.baseUrl}/chat/conversacion`, body);
  }

  obtenerConversaciones(limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/chat/conversaciones`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  obtenerMensajes(idConversacion: number, limite: number = 50, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/chat/conversacion/${idConversacion}/mensajes`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  enviarMensaje(idConversacion: number, contenido: string, tipoMensaje: string, archivoUrl?: string): Observable<any> {
    const body: any = {
      contenido,
      tipo_mensaje: tipoMensaje
    };
    if (archivoUrl) {
      body.archivo_url = archivoUrl;
    }
    return this.http.post(`${this.baseUrl}/chat/conversacion/${idConversacion}/mensaje`, body);
  }

  subirArchivoChat(archivo: File): Observable<any> {
    const formData = new FormData();
    formData.append('archivo', archivo);
    return this.http.post(`${this.baseUrl}/chat/subir-archivo`, formData);
  }

  editarMensaje(idMensaje: number, contenido: string): Observable<any> {
    return this.http.put(`${this.baseUrl}/chat/mensaje/${idMensaje}`, { contenido });
  }

  eliminarMensaje(idMensaje: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/chat/mensaje/${idMensaje}`);
  }

  agregarParticipantes(idConversacion: number, idUsuarios: number[]): Observable<any> {
    return this.http.post(`${this.baseUrl}/chat/conversacion/${idConversacion}/agregar-participantes`, { id_usuarios: idUsuarios });
  }

  salirDeConversacion(idConversacion: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/chat/conversacion/${idConversacion}/salir`);
  }

  silenciarConversacion(idConversacion: number, silenciada: boolean): Observable<any> {
    return this.http.put(`${this.baseUrl}/chat/conversacion/${idConversacion}/silenciar`, { silenciada });
  }

  // ==================== NOTIFICACIONES ====================

  obtenerNotificaciones(limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/notificaciones`, {
      params: { limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  contarNoLeidas(): Observable<any> {
    return this.http.get(`${this.baseUrl}/notificaciones/no-leidas/contar`);
  }

  filtrarPorTipo(tipo: string, limite: number = 20, pagina: number = 1): Observable<any> {
    return this.http.get(`${this.baseUrl}/notificaciones/tipo`, {
      params: { tipo_notificacion: tipo, limite: limite.toString(), pagina: pagina.toString() }
    });
  }

  marcarLeida(idNotificacion: number): Observable<any> {
    return this.http.put(`${this.baseUrl}/notificaciones/${idNotificacion}/leer`, {});
  }

  marcarTodasLeidas(): Observable<any> {
    return this.http.put(`${this.baseUrl}/notificaciones/marcar-todas-leidas`, {});
  }

  eliminarNotificacion(idNotificacion: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/notificaciones/${idNotificacion}`);
  }

  eliminarLeidas(): Observable<any> {
    return this.http.delete(`${this.baseUrl}/notificaciones/eliminar-leidas`);
  }
}

